import { getFingerprint as getFP } from '@thumbmarkjs/thumbmarkjs';
import { apiClient } from '../api/client';
import { hasAnalyticsConsent } from './analytics';

const FP_KEY = 'wds_fp';
let cached: string | null = null;

/**
 * Get or generate a browser fingerprint.
 * Always computed in memory (reading browser signals needs no consent).
 * Only written to localStorage if the user has given analytics consent —
 * persistent cross-session storage on the user's device requires consent under ePrivacy.
 */
export async function getFingerprint(): Promise<string> {
  if (cached) return cached;

  // If consent was given on a prior visit, the value is already in localStorage
  const stored = localStorage.getItem(FP_KEY);
  if (stored) {
    cached = stored;
    apiClient.defaults.headers.common['X-Fingerprint'] = stored;
    return stored;
  }

  try {
    const fp = await getFP();
    cached = fp;
    if (hasAnalyticsConsent()) {
      localStorage.setItem(FP_KEY, fp);
    }
    apiClient.defaults.headers.common['X-Fingerprint'] = fp;
    return fp;
  } catch {
    // Library failed (Brave, Firefox ETP, etc.) — fall back to a session-scoped random ID
    // so the X-Fingerprint header is still set and events count in unique-anon metrics.
    const fallback = 'fp-fallback-' + Math.random().toString(36).slice(2, 11);
    cached = fallback;
    apiClient.defaults.headers.common['X-Fingerprint'] = fallback;
    return fallback;
  }
}

/**
 * Persist the in-memory fingerprint to localStorage now that consent has been granted.
 * Call this immediately after the user accepts the cookie banner.
 */
export function persistFingerprintOnConsent(): void {
  if (cached) {
    localStorage.setItem(FP_KEY, cached);
  }
}

/**
 * Return the fingerprint from memory or localStorage without recomputing.
 * Falls back to 'unknown' for users who haven't consented yet.
 */
export function getCachedFingerprint(): string {
  return cached ?? localStorage.getItem(FP_KEY) ?? 'unknown';
}
